import React, { Component } from 'react'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import { AxiosInstance } from 'axios'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Meta, RatatoskRequest, getLocalizedLabel } from '../../common'
import { Button, Checkbox, Container, Dimmer, Dropdown, Grid, Header, Icon, Input, Label, Loader, Message, Modal, Progress, Segment } from 'semantic-ui-react'
import FileSelect from '../FileSelect'
import CNCDictCandidatesItem from './CNCDictCandidatesItem'
import CNCDictCandidatesExamples from './CNCDictCandidatesExamples'
import { addForms } from './CNCDictSublemma'
import { Md5 } from 'ts-md5'

interface CNCDictCandidatesProps extends WithTranslation, RouteComponentProps {
    meta: Meta,
    http: AxiosInstance
}

interface CNCDictCandidatesState {
    patternList: any[],
    filePath: string,
    candidates: any[],
    loading: boolean,
    errorMsg: string,
    initialized: boolean,
    curItem: number|null,
    ignoreFormsDialog: boolean,
    ignoredForms: string[],
    quickSearch: string
}

const ROOT_TYPE = ':cncdict'
const ROOT_PATH = ROOT_TYPE.replaceAll(':','/').replace(/^\//, '_/')
const BATCH_SIZE = 100

class CNCDictCandidates extends Component<CNCDictCandidatesProps, CNCDictCandidatesState> {

    state = {
        patternList: [],
        filePath: '',
        candidates: [] as any[],
        loading: false,
        errorMsg: '',
        initialized: false,
        curItem: null,
        ignoreFormsDialog: false,
        ignoredForms: [] as string[],
        quickSearch: ''
    }

    parseCandidateList(lines) {
        const list: any[] = []
        let current: any = { form: '', fq: 0, patterns: []}
        for (let line of lines) {
            const m = line.match(/^([^\s]+)\t([0-9]+)/)
            if (m) {
                if (current.form) list.push(current)
                current = { form: m[1], fq: parseInt(m[2]), patterns: []}
            } else {
                const mp = line.trim().split('\t')
                const sublemma = mp.shift().split('&&')
                const patternList = mp.shift().split('&&')
                const pattern = patternList.length > 2 ? patternList.slice(0, 2) : patternList
                const fq = parseInt(mp.shift())
                current.patterns.push({
                    key: window.crypto.randomUUID(),
                    sublemma: sublemma,
                    pattern: pattern,
                    fq: fq,
                    original: true,
                    selected: false,
                    created: null,
                    forms: mp.map((i) => {
                        const p = i.split(' ')
                        return { form: p[0], fq: parseFloat(p[1])}
                    })
                })
            }
        }
        if (current.form) list.push(current)
        return list
    }

    loadPath(path) {
        const { t, meta } = this.props
        this.setState({loading: true})
        const url = 'files/'+path
        this.props.http.get(url)
            .then(response => {
                const data = response.data.data
                if (typeof(data)==='string') {
                    const candidates = this.parseCandidateList(data.split('\n').filter((l: string) => l.trim()))
                    this.setState({candidates: candidates, errorMsg: '', loading: false, initialized: true, curItem: null})
                    if (path !== sessionStorage.getItem('cncdictcandidates_file')) {
                        sessionStorage.setItem('cncdictcandidates_file', path)
                        //sessionStorage.removeItem('cncdictcandidates_item')
                    }
                    if (path !== localStorage.getItem('cncdictcandidates_file')) {
                        localStorage.setItem('cncdictcandidates_file', path)
                        //localStorage.removeItem('cncdictcandidates_item')
                    }
                } else
                    this.setState({candidates: [], errorMsg: '', loading: false, initialized: true, curItem: null})
                this.nextUnknown()
            }).catch(err => {
                if (err.response) {
                    this.setState({errorMsg: err.response.data.message||err.response.data, loading: false, candidates: [], curItem: null})
                } else {
                    console.log(err)
                    this.setState({errorMsg: t('Unknown error'), loading: false, candidates: [], curItem: null})
                }
            })
    }

    loadPatternList() {
        const { t } = this.props
        let request: RatatoskRequest = {
            query: {
                'type': ':cncdict:pattern'
            },
            page: { from: 0, size: 10000 },
            feats: [
                ":form:cncdict:base_ending",
                ":form:cncdict:base_tag",
                ":form:cncdict:base_constraints",
                ":form:cncdict:ending"
            ]
        }
        this.setState({loading: true})
        const url = 'cunits/_view'
        this.props.http.post(url, request)
            .then(response => {
                const list = response.data.data.sort(i => i._name)
                this.setState({patternList: list, errorMsg: '', loading: false})
            }).catch(err => {
                if (err.response) {
                    this.setState({errorMsg: err.response.data.message||err.response.data, loading: false})
                } else {
                    console.log(err)
                    this.setState({errorMsg: t('Unknown error'), loading: false})
                }
            })
    }

    async rescanCandidates(formsCreated: string[]=[]) {
        const { candidates } = this.state
        let queryList: number[] = []
        let createdList: number[] = []
        let unknown_found = false
        let last = 0
        for (let i = 0; i < candidates.length; i++) {
            last = i
            // ES is not realtime, rescan won't notice forms that have just been added!
            if (formsCreated.includes(candidates[i].form)) {
                createdList.push(i)
                continue
            }
            if (!candidates[i]['db']?.length)
                queryList.push(i)
            if (queryList.length >= BATCH_SIZE) {
                unknown_found = await this.updateCandidates(queryList, createdList)
                queryList = []
                if (unknown_found)
                    break
            }
        }
        if (queryList.length)
            await this.updateCandidates(queryList, createdList)
        this.setState({loading: false})
    }

    async updateCandidates(queryList: number[], createdList: number[]) {
        const { candidates } = this.state
        const formList = queryList.map(i => candidates[i].form)
        const request: RatatoskRequest = {
            query: {
                'type': ':cncdict:form',
                'feats': [
                    {type: ':form:attr:cnc:w:word', value: formList, ci: false}
                ]
            },
            page: { from: 0, size: 10000 },
            feats: []
        }
        const url = 'cunits/_view'
        const res = await this.props.http.post(url, request)
            .then(response => response.data?.data.length ? response.data.data.reduce((acc, u) => {
                const form = u['_name']
                if (!Array.isArray(acc[form])) 
                    acc[form] = []
                acc[form].push(u._path)
                return acc
            }, {}) : [])
        let unknown_found = false
        queryList.forEach(i => {
            const form = candidates[i].form
            candidates[i]['db'] = res[form] || []
            if (!res[form])
                unknown_found = true
        })
        createdList.forEach(i => candidates[i]['db'] = [{_created: true}])
        this.setState({candidates: candidates, ignoredForms: []})
        return unknown_found
    }

    /*async testCandidates() {
        const { candidates } = this.state
        this.setState({loading: true})
        for (let i = 0; i < candidates.length; i += BATCH_SIZE) {
            const chunk = candidates.slice(i, i + BATCH_SIZE)
            const form = chunk.map(c => c.form)
            const request: RatatoskRequest = {
                query: {
                    'type': ':cncdict:form',
                    'feats': [
                        {type: ':form:attr:cnc:w:word', value: form, ci: true}
                    ]
                },
                page: { from: 0, size: 10000 },
                feats: []
            }
            const url = 'cunits/_view'
            const res = await this.props.http.post(url, request)
                .then(response => response.data?.data.length ? response.data.data.reduce((acc, u) => {
                    const form = u['_name'].toLowerCase()
                    if (!Array.isArray(acc[form])) 
                        acc[form] = []
                    acc[form].push(u._path)
                    return acc
                }, {}) : [])
            const last = (i + BATCH_SIZE) > candidates.length ? candidates.length : i + BATCH_SIZE
            for (let n = i; n < last; n++) {
                const form = candidates[n].form
                candidates[n]['db'] = res[form] || []
            }
            this.setState({candidates: candidates})
        }
        this.setState({loading: false})
    }*/

    async componentDidMount(): Promise<void> {
        this.loadPatternList()
        this.loadPath(this.props.match.params[0] || ROOT_PATH)
    }

    componentDidUpdate(prevProps: Readonly<CNCDictCandidatesProps>, prevState: Readonly<CNCDictCandidatesState>, snapshot?: any): void {
        const { loading: loadingPath } = this.state
        if (!loadingPath) {
            const path = this.props.match.params[0]
            if (path!==prevProps.match.params[0]) {
                if (path)
                    this.loadPath(path)
                else
                    this.setState({candidates: [], ignoredForms: []})
            }
        }
    }

    async nextUnknown(formsCreated: string[]=[], rev=false) {
        const { candidates, curItem } = this.state
        if (!candidates.length)
            return
        if (!rev) await this.rescanCandidates(formsCreated)
        const inc = rev ? -1 : 1
        let i = curItem!==null ? curItem+inc : (rev ? candidates.length-1 : 0)
        while (0 <= i && i < candidates.length) {
            if (!candidates[i]['db']?.length)
                break
            i = i + inc
        }
        const next = 0 <= i && i < candidates.length ? i : null
        this.setState({curItem: next})
        //localStorage.setItem('cncdictcandidates_item', String(next))
    }

    prevUnknown(formsCreated: string[]=[]) {
        this.nextUnknown(formsCreated, true)
    }

    async saveIgnoredForms(forms: string[], next: boolean = true) {
        const { http, t } = this.props
        const { candidates, curItem } = this.state
        let errors: string[] = []
        this.setState({loading: true})
        const formList: any[] = forms.map(w => ({word: w, ignored: true}))
        const res = await addForms(http, formList)
        if (typeof res === 'string') errors.push(res)
        else if (!res.length) errors.push(t("Empty response."))
        else {
            for (let i of res) {
                if (i._error) errors.push(i._error)
            }
        }
        this.setState({loading: false, errorMsg: errors.join("\n")})
        if (next && !errors.length)
            this.nextUnknown(forms, false)
    }


    render() {
        const { i18n, t, meta, history } = this.props
        const { candidates, curItem, patternList, loading, ignoreFormsDialog, ignoredForms, quickSearch } = this.state

        const initialType = this.props.match.params[0].replaceAll('/',':').replace(/^_:/, ':')

        const changePath = (newtype: string) => {
            if (newtype) {
                history.push('/comp/cncdict/candidates/'+newtype.replaceAll(':', '/').replace(/^\//, '_/'))
            } else {
                history.push('/comp/cncdict/candidates/')
            }
        }

        const setSubLemma = (i: number, subi: number, value: string) => {
            if (curItem===null) return
            candidates[curItem].patterns[i].sublemma[subi] = value
            this.setState({candidates: candidates})
        }

        const toggleSelect = (i: number) => {
            if (curItem===null) return
            candidates[curItem].patterns[i].selected = !candidates[curItem].patterns[i].selected
            this.setState({candidates: candidates})
        }

        const setPattern = (i: number, subi: number, value: string) => {
            if (curItem===null) return
            candidates[curItem].patterns[i].pattern[subi] = value
            this.setState({candidates: candidates})
        }

        const setCreated = (i: number, value = true) => {
            if (curItem===null) return
            candidates[curItem].patterns[i].created = value
            this.setState({candidates: candidates})
        }

        const addPattern = () => {
            if (curItem===null) return
            candidates[curItem].patterns.unshift({
                key: window.crypto.randomUUID(),
                sublemma: [candidates[curItem].form],
                pattern: ['F%_cizí_slovo'],
                fq: null,
                original: false,
                created: null,
                selected: true,
                forms: []
            })
            this.setState({candidates: candidates})
        }

        const toggleIgnored = (form: string) => {
            if (ignoredForms.includes(form)) {
                this.setState({ignoredForms: ignoredForms.filter(x => x !== form)})
            } else {
                this.setState({ignoredForms: [...ignoredForms, form]})
            }
        }

        const openQuickSearch = () => {
            window.open(location.pathname.replace(/\/comp\/cncdict\/candidates\/.*$/,"")+'/search/_cncdict_dict/'+quickSearch, '_blank')
        }

        //const checked = candidates.filter(i => typeof i['db'] !== 'undefined').length
        //const found = candidates.filter(i => i['db'] && i['db'].length).length

        const curCandidate = curItem!==null ? candidates[curItem] : null
        const formList = curCandidate ? [... new Set(curCandidate.patterns.map(p => p.forms.map(f => f.form)).flat() as string[])] : []
        const otherFormList = formList.filter(x => x!==curCandidate.form)

        const someSelected = curCandidate ? curCandidate.patterns.some(x => x.selected) : false
        const someCreated = curCandidate ? curCandidate.patterns.some(x => x.created) : false
        const someSelectedNotCreated = curCandidate ? curCandidate.patterns.some(x => x.selected && !x.created) : false

        return(
            <Container>
            <Modal size="tiny" open={ignoreFormsDialog} closeIcon={true} onClose={() => this.setState({ignoreFormsDialog: false})} centered={false}>
                <Modal.Content>
                    <Grid>
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Header as="h2">{t('Ignore associated forms')}</Header>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Button type="button" size="tiny" color="blue" icon="plus" onClick={() => this.setState({ignoredForms: otherFormList})} title={t('select all')}/>
                                <Button type="button" size="tiny" color="red" icon="delete" onClick={() => this.setState({ignoredForms: []})} title={t('unselect all')}/>
                            </Grid.Column>
                        </Grid.Row>
                        { otherFormList.map(f => (
                        <Grid.Row>
                            <Grid.Column width={16}>
                                <Checkbox checked={ignoredForms.includes(f)} onChange={() => toggleIgnored(f)}/> {f}
                            </Grid.Column>
                        </Grid.Row>
                        ))}
                        <Grid.Row>
                            <Grid.Column width={8}>
                                <Button type='button' color="yellow" icon="arrow left" content={t('Back')} onClick={() => this.setState({ignoreFormsDialog: false})}/>
                            </Grid.Column>
                            <Grid.Column width={8} textAlign='right'>
                                <Button type='button' color="red" content={t('Ignore selected')} disabled={!ignoredForms.length}
                                    onClick={() => this.saveIgnoredForms(ignoredForms, false)}/>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Modal.Content>
            </Modal>
            <Grid>
                {this.state.errorMsg ?
                (
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Message error icon='exclamation triangle' header={t('Error')} content={this.state.errorMsg}/>
                        </Grid.Column>
                    </Grid.Row>
                )
                : null }
                <Grid.Row>
                    <Grid.Column width={16}>
                        <FileSelect {...this.props} rootType={ROOT_TYPE} setSelected={changePath} initialType={initialType}/>
                    </Grid.Column>
                </Grid.Row>
                { candidates.length ?
                <Grid.Row>
                    <Grid.Column width={1}>
                        <Button type="button" color="blue" icon="angle left" onClick={() => this.prevUnknown()}/>
                    </Grid.Column>
                    <Grid.Column width={14}>
                    { curItem !== null ?
                        <>{t('Candidate {{num}} of {{count}}', {num: curItem+1, count: candidates.length})}
                        <Progress value={curItem!==null ? (curItem+1) : 0} total={candidates.length} size="tiny" color="blue" active={loading}/>
                        </>
                    : candidates.length ?
                        <>{t('No more unknown forms.')}</>
                    : null }
                    </Grid.Column>
                    <Grid.Column width={1} textAlign="right">
                        <Button type="button" color="blue" icon="angle right" onClick={() => this.nextUnknown()}/>
                    </Grid.Column>
                </Grid.Row>
                : null }
                { curCandidate && curItem!==null ?
                <>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Header as="h2">{curCandidate.form} ({curCandidate.fq})</Header>
                    </Grid.Column>
                </Grid.Row>
                { formList.length ?
                <Grid.Row>
                    <Grid.Column width={16}>
                        <CNCDictCandidatesExamples {...this.props} forms={formList} basePath={this.props.match.params[0]}/>
                    </Grid.Column>
                </Grid.Row>
                : null }
                <Grid.Row>
                    <Grid.Column width={6}>
                        <Button type="button" color="blue" icon="plus" onClick={() => addPattern()} title={t('Custom paradigm')}/>
                        <Button type="button" color="red" onClick={() => this.setState({ignoreFormsDialog: true})} content={t('Ignore associated forms')}/>
                    </Grid.Column>
                    <Grid.Column width={4} textAlign='center'>
                        <Input fluid value={quickSearch} placeholder={t('search form')}
                            onChange={(e, {value}) => this.setState({quickSearch: value})} onKeyDown={(e) => { if (e.key==='Enter') openQuickSearch() }}
                            action={{icon: 'search', onClick: openQuickSearch , title: t('Form search in new tab')}} labelPosition='right'/>
                    </Grid.Column>
                    <Grid.Column width={6} textAlign='right'>
                        { someCreated ?
                            <Button type="button" color="blue" icon="play" disabled={someSelectedNotCreated} onClick={() => this.nextUnknown()}/>
                        :
                            <Button type="button" color="red" onClick={() => this.saveIgnoredForms([curCandidate.form], true)}
                                content={t('Ignore')} icon="play" labelPosition='right' disabled={someSelected}/>
                        }
                    </Grid.Column>
                </Grid.Row>
                { curCandidate.patterns.map((p: any, i: number) => (
                    <CNCDictCandidatesItem {...this.props} key={p.key} candidates={candidates} candIdx={curItem} itemIdx={i} patternList={patternList} setCreated={setCreated}
                        setSubLemma={setSubLemma} setPattern={setPattern} toggleSelect={toggleSelect} nextUnknown={(formList) => this.nextUnknown(formList, false)}/>
                ))}
                </>
                : null }
            </Grid>
            </Container>
        )
    }
}

export default withTranslation()(CNCDictCandidates)